<script setup lang="ts">
// 계정: 가입(3단계)·계정 화면·탈퇴(3단계). 브랜드 빌드 전용. accounts.md·prototype.html 포팅.
// 이메일 매직링크로 동작(dev는 코드를 바로 보여 줌). 애플/구글은 포털 연결 후.
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAccountStore, makeRecoveryKey } from '../../stores/account'

const { t } = useI18n()
const acct = useAccountStore()
const emit = defineEmits<{ close: []; toast: [msg: string]; openAdmin: [] }>()

// 백업/복원
const lastBk = ref<{ updated_at: string } | null>(null)
const keyInput = ref('')
onMounted(async () => { if (acct.signedIn) lastBk.value = await acct.lastBackup() })
async function doBackup() {
  if (busy.value) return; busy.value = true
  try { await acct.backup(keyInput.value.trim() || undefined); lastBk.value = { updated_at: new Date().toISOString() }; emit('toast', t('account.backedUp')) }
  catch (e: any) { emit('toast', e.message === 'need_key' ? t('account.needKey') : e.message) }
  finally { busy.value = false }
}
async function doRestore() {
  const k = keyInput.value.trim() || acct.recoveryKey || ''
  if (!k) { emit('toast', t('account.needKey')); return }
  if (busy.value) return; busy.value = true
  try { const r = await acct.restore(k); emit('toast', t('account.restored', { n: r.entries })) }
  catch (e: any) { emit('toast', e.status === 404 ? t('account.noBackup') : t('account.keyWrong')) }
  finally { busy.value = false }
}

type Mode = 'panel' | 'signup' | 'delete'
const mode = ref<Mode>('panel')
const step = ref(0)
const busy = ref(false)
const errMsg = ref('')

// 가입 폼
const email = ref('')
const code = ref('')
const devCode = ref('') // dev에서만: 보낸 코드를 화면에 표시
const emailHint = ref('')
const consents = ref<Record<string, boolean>>({ terms: false, privacy: false, age: false, contribute: false, marketing: false })
const recoveryKey = ref<string[]>([])
const savedKey = ref(false)
const reqOk = computed(() => consents.value.terms && consents.value.privacy && consents.value.age)

// 탈퇴 폼
const localWipe = ref(false)
const reasons = ref<Record<string, boolean>>({})
const reasonText = ref('')
const REASONS = ['rare', 'reply', 'privacy', 'price', 'other_app', 'left_job']

function openSignup() { mode.value = 'signup'; step.value = 0; errMsg.value = ''; email.value = ''; code.value = ''; devCode.value = '' }
function openDelete() { mode.value = 'delete'; step.value = 0; errMsg.value = ''; localWipe.value = false; reasons.value = {}; reasonText.value = '' }
function back() { if (step.value > 0) step.value--; else mode.value = 'panel' }

async function sendCode() {
  if (busy.value) return
  errMsg.value = ''; busy.value = true
  try {
    const r = await acct.startEmail(email.value.trim())
    emailHint.value = r.email_hint; devCode.value = r.dev_code || ''
    step.value = 1
  } catch (e: any) { errMsg.value = e.message } finally { busy.value = false }
}
function toRecoveryKey() {
  if (!reqOk.value) { errMsg.value = t('account.needRequired'); return }
  if (!code.value.trim()) { errMsg.value = t('account.needCode'); return }
  recoveryKey.value = makeRecoveryKey(); savedKey.value = false; step.value = 2; errMsg.value = ''
}
function copyKey() { navigator.clipboard?.writeText(recoveryKey.value.join(' ')).catch(() => {}); emit('toast', t('account.keyCopied')) }
async function finishSignup() {
  if (!savedKey.value || busy.value) return
  errMsg.value = ''; busy.value = true
  try {
    const cs = Object.entries(consents.value).map(([kind, granted]) => ({ kind, granted }))
    await acct.verifyEmail({ email: email.value.trim(), code: code.value.trim(), age_confirmed: true, consents: cs })
    acct.setRecoveryKey(recoveryKey.value) // 복구 키를 메모리에 — 바로 백업 가능(저장은 안 함)
    mode.value = 'panel'; emit('toast', t('account.signedUp'))
  } catch (e: any) { errMsg.value = e.message; if (e.code === 'bad_code' || e.code === 'expired_code') step.value = 1 } finally { busy.value = false }
}
async function doLogout() { busy.value = true; try { await acct.logout(); emit('toast', t('account.loggedOut')) } finally { busy.value = false } }
async function doCancelDeletion() { busy.value = true; try { await acct.cancelDeletion(); emit('toast', t('account.deletionCancelled')) } catch (e: any) { emit('toast', e.message) } finally { busy.value = false } }

async function finishDelete() {
  if (busy.value) return
  busy.value = true; errMsg.value = ''
  try {
    const rt = await acct.reauth()
    const codes = Object.entries(reasons.value).filter(([, v]) => v).map(([k]) => k)
    await acct.deleteAccount(rt, codes, reasonText.value.trim() || undefined)
    mode.value = 'panel'
    emit('toast', t(localWipe.value ? 'account.deletedWipe' : 'account.deletedKeep'))
  } catch (e: any) { errMsg.value = e.message } finally { busy.value = false }
}

const fmtDay = (iso?: string | null) => { if (!iso) return ''; const d = new Date(iso); return `${d.getMonth() + 1}월 ${d.getDate()}일` }
</script>

<template>
  <div class="ac-scrim" @click="emit('close')"></div>
  <div class="ac" role="dialog" aria-modal="true">
    <div class="ac-top">
      <b>{{ mode === 'signup' ? t('account.signupTitle') : mode === 'delete' ? t('account.deleteTitle') : t('account.title') }}</b>
      <button class="ac-x" @click="mode === 'panel' ? emit('close') : back()">{{ mode === 'panel' ? '×' : t('account.back') }}</button>
    </div>

    <div v-if="mode !== 'panel'" class="ac-steps"><i v-for="n in 3" :key="n" :class="{ on: n - 1 <= step }"></i></div>
    <p v-if="errMsg" class="ac-err">{{ errMsg }}</p>

    <!-- ── 패널 ── -->
    <template v-if="mode === 'panel'">
      <template v-if="!acct.signedIn">
        <div class="ac-card">
          <h4>{{ t('account.localOnly') }}</h4>
          <p>{{ t('account.whySignup') }}</p>
          <button class="ac-btn" @click="openSignup">{{ t('account.signupBackup') }}</button>
        </div>
        <p class="ac-sub">{{ t('account.keepData') }}</p>
      </template>
      <template v-else>
        <div v-if="acct.pendingDeletion" class="ac-pend">
          <span>{{ t('account.pendingUntil', { d: fmtDay(acct.user!.deletion_due_at) }) }}</span>
          <button class="ac-btn ghost sm" @click="doCancelDeletion">{{ t('account.cancelDeletion') }}</button>
        </div>
        <div class="ac-card">
          <div class="ac-row"><span class="k">{{ t('account.login') }}</span><span class="v">{{ acct.user!.provider }} · {{ acct.user!.email_hint }}</span></div>
          <div class="ac-row"><span class="k">{{ t('account.plan') }}</span><span class="v">{{ acct.user!.plan === 'free' ? 'Free' : 'Pro' }}</span></div>
          <div class="ac-row"><span class="k">{{ t('account.contribute') }}</span><input type="checkbox" role="switch" :aria-label="t('account.contribute')" @change="acct.setContribute(($event.target as HTMLInputElement).checked)"></div>
        </div>
        <div class="ac-card">
          <h4>{{ t('account.backup') }}</h4>
          <p>{{ lastBk ? t('account.lastBackup', { d: fmtDay(lastBk.updated_at) }) : t('account.noBackup') }}</p>
          <input v-if="!acct.recoveryKey" v-model="keyInput" class="ac-key-in" :placeholder="t('account.keyPlaceholder')" autocomplete="off" />
          <div class="ac-bk-row">
            <button class="ac-btn ghost sm" :disabled="busy" @click="doBackup">{{ t('account.backupNow') }}</button>
            <button class="ac-btn ghost sm" :disabled="busy" @click="doRestore">{{ t('account.restore') }}</button>
          </div>
        </div>
        <button v-if="acct.user?.role === 'admin'" class="ac-btn ghost" @click="emit('openAdmin')">운영 콘솔</button>
        <button class="ac-btn ghost" @click="doLogout">{{ t('account.logout') }}</button>
        <button v-if="!acct.pendingDeletion" class="ac-btn link" @click="openDelete">{{ t('account.delete') }}</button>
      </template>
    </template>

    <!-- ── 가입 ── -->
    <template v-else-if="mode === 'signup'">
      <template v-if="step === 0">
        <p class="ac-sub">{{ t('account.noName') }}</p>
        <button class="ac-btn ghost" disabled>{{ t('account.apple') }} <em>{{ t('account.soon') }}</em></button>
        <button class="ac-btn ghost" disabled>{{ t('account.google') }} <em>{{ t('account.soon') }}</em></button>
        <label class="ac-f"><span>{{ t('account.email') }}</span><input v-model="email" type="email" inputmode="email" autocomplete="email" placeholder="you@example.com" @keydown.enter="sendCode" /></label>
        <button class="ac-btn" :disabled="busy || !email.includes('@')" @click="sendCode">{{ t('account.sendCode') }}</button>
      </template>

      <template v-else-if="step === 1">
        <p class="ac-sub">{{ t('account.codeSent', { hint: emailHint }) }}</p>
        <div v-if="devCode" class="ac-dev">dev 코드: <b>{{ devCode }}</b></div>
        <label class="ac-f"><span>{{ t('account.code') }}</span><input v-model="code" inputmode="numeric" maxlength="6" placeholder="6자리" /></label>
        <label class="chk all"><input type="checkbox" :checked="reqOk && consents.contribute && consents.marketing" @change="(e) => { const v = (e.target as HTMLInputElement).checked; ['terms','privacy','age','contribute','marketing'].forEach(k => consents[k] = v) }"> {{ t('account.agreeAll') }}</label>
        <label class="chk"><input type="checkbox" v-model="consents.terms"><span><span class="tag">{{ t('account.req') }}</span>{{ t('account.terms') }}</span></label>
        <label class="chk"><input type="checkbox" v-model="consents.privacy"><span><span class="tag">{{ t('account.req') }}</span>{{ t('account.privacy') }}<small>{{ t('account.privacyNote') }}</small></span></label>
        <label class="chk"><input type="checkbox" v-model="consents.age"><span><span class="tag">{{ t('account.req') }}</span>{{ t('account.age') }}</span></label>
        <label class="chk"><input type="checkbox" v-model="consents.contribute"><span><span class="tag opt">{{ t('account.opt') }}</span>{{ t('account.contributeAgree') }}<small>{{ t('account.contributeNote') }}</small></span></label>
        <label class="chk"><input type="checkbox" v-model="consents.marketing"><span><span class="tag opt">{{ t('account.opt') }}</span>{{ t('account.marketing') }}</span></label>
        <button class="ac-btn" :disabled="!reqOk || !code.trim()" @click="toRecoveryKey">{{ t('account.next') }}</button>
      </template>

      <template v-else>
        <div class="ac-card plain"><h4>{{ t('account.saveKey') }}</h4><p>{{ t('account.keyWhy') }}</p></div>
        <div class="rk"><span v-for="(w, i) in recoveryKey" :key="i"><b>{{ i + 1 }}</b>{{ w }}</span></div>
        <button class="ac-btn ghost" @click="copyKey">{{ t('account.copy') }}</button>
        <label class="chk plain"><input type="checkbox" v-model="savedKey"> {{ t('account.savedKey') }}</label>
        <button class="ac-btn" :disabled="!savedKey || busy" @click="finishSignup">{{ t('account.finishSignup') }}</button>
      </template>
    </template>

    <!-- ── 탈퇴 ── -->
    <template v-else>
      <template v-if="step === 0">
        <div class="ac-card"><h4>{{ t('account.onDelete') }}</h4>
          <ul class="ok-list">
            <li>{{ t('account.delSync') }}</li><li>{{ t('account.del7d') }}</li><li>{{ t('account.delContrib') }}</li><li>{{ t('account.delBilling') }}</li>
          </ul>
        </div>
        <div class="ac-card"><h4>{{ t('account.localQ') }}</h4><p>{{ t('account.localQnote') }}</p>
          <div class="radio"><label><input type="radio" :value="false" v-model="localWipe"> {{ t('account.keep') }}</label><label><input type="radio" :value="true" v-model="localWipe"> {{ t('account.wipe') }}</label></div>
        </div>
        <button class="ac-btn" @click="step = 1">{{ t('account.next') }}</button>
      </template>
      <template v-else-if="step === 1">
        <div class="ac-card plain"><h4>{{ t('account.whyLeave') }}</h4><p>{{ t('account.whyLeaveNote') }}</p></div>
        <div class="chips"><button v-for="r in REASONS" :key="r" :class="{ sel: reasons[r] }" @click="reasons[r] = !reasons[r]">{{ t('account.reason_' + r) }}</button></div>
        <textarea class="ac-ta" v-model="reasonText" :placeholder="t('account.reasonMore')"></textarea>
        <button class="ac-btn" @click="step = 2">{{ t('account.next') }}</button>
        <button class="ac-btn link" @click="step = 2">{{ t('account.skip') }}</button>
      </template>
      <template v-else>
        <div class="ac-card"><h4>{{ t('account.lastCheck') }}</h4><p>{{ t('account.lastCheckNote') }}</p></div>
        <button class="ac-btn danger" :disabled="busy" @click="finishDelete">{{ t('account.doDelete') }}</button>
      </template>
    </template>
  </div>
</template>

<style scoped>
.ac-scrim { position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 70; }
.ac { position: fixed; left: 50%; top: 50%; transform: translate(-50%,-50%); z-index: 71; width: min(440px, 92vw); max-height: 88vh; overflow-y: auto;
  background: var(--card); border-radius: 22px; padding: 18px 20px calc(22px + env(safe-area-inset-bottom)); box-shadow: 0 20px 60px rgba(0,0,0,.3); }
.ac-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.ac-top b { font-size: 18px; }
.ac-x { border: 0; background: none; color: var(--pencil); font: inherit; font-size: 18px; cursor: pointer; padding: 4px 8px; }
.ac-steps { display: flex; gap: 6px; margin: -4px 0 16px; }
.ac-steps i { flex: 1; height: 4px; border-radius: 2px; background: var(--line); }
.ac-steps i.on { background: var(--ink); }
.ac-err { color: #A8262B; font-size: 13px; margin: 0 0 10px; }
.ac-card { background: var(--paper-2); border-radius: 14px; padding: 14px; margin-bottom: 12px; }
.ac-card.plain { background: none; padding: 0 2px; }
.ac-card h4 { margin: 0 0 6px; font-size: 15px; }
.ac-card p { margin: 0; color: var(--pencil); font-size: 13px; line-height: 1.55; }
.ac-row { display: flex; align-items: center; gap: 10px; min-height: 48px; border-bottom: 1px solid var(--line); font-size: 14px; }
.ac-row:last-child { border-bottom: 0; }
.ac-row .k { flex: 1; } .ac-row .v { color: var(--pencil); font-size: 13px; text-align: right; }
.ac-btn { width: 100%; min-height: 50px; border: 0; border-radius: 14px; font: inherit; font-size: 15px; font-weight: 700; background: var(--ink); color: var(--paper); cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 10px; }
.ac-btn.ghost { background: none; color: var(--ink); border: 1px solid var(--line); font-weight: 500; }
.ac-btn.link { background: none; color: var(--pencil); font-weight: 500; min-height: 44px; }
.ac-btn.danger { background: none; color: #A8262B; border: 1px solid #A8262B; font-weight: 500; }
.ac-btn.sm { width: auto; min-height: 38px; padding: 0 12px; margin: 0; }
.ac-btn[disabled] { opacity: .45; cursor: default; }
.ac-btn em { font-style: normal; font-size: 11px; opacity: .7; }
.ac-sub { color: var(--pencil); font-size: 13px; margin: 0 0 12px; line-height: 1.55; }
.ac-f { display: block; margin: 10px 0; }
.ac-f span { display: block; font-size: 13px; color: var(--pencil); margin-bottom: 6px; }
.ac-f input { width: 100%; min-height: 48px; border: 1px solid var(--line); background: var(--paper); border-radius: 12px; padding: 0 14px; font: inherit; font-size: 16px; color: var(--ink); }
.ac-dev { background: rgba(224,168,0,.14); border-radius: 10px; padding: 8px 12px; font-size: 13px; margin-bottom: 8px; }
.chk { display: flex; align-items: flex-start; gap: 10px; min-height: 46px; padding: 8px 0; border-bottom: 1px solid var(--line); font-size: 14px; cursor: pointer; }
.chk.all { font-weight: 700; border-bottom: 2px solid var(--ink); }
.chk.plain { border: 0; }
.chk input { width: 22px; height: 22px; margin: 0; flex: none; accent-color: var(--ink); }
.chk small { display: block; color: var(--pencil); font-size: 12px; margin-top: 2px; }
.tag { font-size: 11px; font-weight: 700; padding: 1px 6px; border-radius: 4px; background: var(--card); margin-right: 4px; }
.tag.opt { color: var(--pencil); }
.rk { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; background: var(--paper-2); border-radius: 14px; padding: 14px; font-size: 14px; margin: 10px 0; }
.rk span b { color: var(--pencil); font-weight: 500; margin-right: 4px; }
.ac-pend { display: flex; gap: 10px; align-items: center; background: rgba(224,168,0,.14); border-radius: 12px; padding: 12px; font-size: 13px; margin-bottom: 12px; }
.ac-pend span { flex: 1; }
.ok-list { margin: 0; padding-left: 18px; font-size: 13px; line-height: 1.8; }
.radio { display: flex; gap: 8px; margin-top: 8px; }
.radio label { flex: 1; display: flex; align-items: center; gap: 8px; min-height: 46px; padding: 0 12px; border: 1px solid var(--line); border-radius: 12px; cursor: pointer; font-size: 13px; }
.radio label:has(input:checked) { border-color: var(--ink); background: var(--paper-2); }
.chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0 12px; }
.chips button { min-height: 40px; border: 1px solid var(--line); background: var(--card); color: var(--ink); border-radius: 999px; padding: 0 14px; font: inherit; font-size: 13px; cursor: pointer; }
.chips button.sel { background: var(--ink); color: var(--paper); border-color: var(--ink); }
.ac-ta { width: 100%; min-height: 72px; border: 1px solid var(--line); background: var(--paper); border-radius: 12px; padding: 12px; font: inherit; font-size: 15px; color: var(--ink); resize: vertical; }
.ac-key-in { width: 100%; min-height: 44px; border: 1px solid var(--line); background: var(--paper); border-radius: 12px; padding: 0 12px; font: inherit; font-size: 14px; color: var(--ink); margin: 8px 0; }
.ac-bk-row { display: flex; gap: 8px; }
.ac-bk-row .ac-btn.sm { flex: 1; }
</style>
